import { type ReactNode, type TdHTMLAttributes, type ThHTMLAttributes } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { TableHead, TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { SortDirection } from './use-sorted-rows';

/** Pins the name column while the other columns scroll under it (left-4 clears the 16px edge gutter). */
export const STICKY_HEAD = 'sticky left-4 z-20';
// A name cell with a sub-row under it starts its row rule at the tree line, so the rule doesn't stick out past the branch.
export const STICKY_CELL =
  'sticky left-4 z-10 bg-white [tr:has(+tr>.tree-sub)>&]:border-b-0 [tr:has(+tr>.tree-sub)>&]:bg-[linear-gradient(var(--color-gray-300),var(--color-gray-300))] [tr:has(+tr>.tree-sub)>&]:bg-[length:calc(100%-3px)_1px] [tr:has(+tr>.tree-sub)>&]:bg-[position:right_bottom] [tr:has(+tr>.tree-sub)>&]:bg-no-repeat';

/** Short vertical rule at the left of a cell, as in the Figma tables. */
export const DIVIDER = 'relative pl-2.5 before:absolute before:left-0 before:top-1/2 before:h-4 before:w-px before:-translate-y-1/2 before:bg-gray-500';

// `Table`'s own wrapper div bakes in a rounded border + overflow we don't want here, and that div's
// className isn't configurable from outside (it only reaches the <table>), so the root stays a plain
// table to keep the Figma-matched bleed layout; the cells below are rebuilt on TableHead/TableCell.
export function AnalyticsTable({ children }: { children: ReactNode }) {
  return (
    <div className='-mx-4 mt-0 overflow-x-auto'>
      <table className='w-full border-separate border-spacing-0 font-inter'>{children}</table>
    </div>
  );
}

/** 16px gutter cell at each end of a row, so row rules stop short of the page edge like in Figma. */
export function EdgeHead({ rowSpan, sticky = false }: { rowSpan?: number; sticky?: boolean }) {
  return (
    <TableHead aria-hidden='true' rowSpan={rowSpan} className={cn('h-auto w-4 min-w-4 border-x-0 border-t border-b border-gray-300 bg-gray-50 p-0', sticky && 'sticky left-0 z-20')} />
  );
}

export function EdgeCell({ sticky = false }: { sticky?: boolean }) {
  return <TableCell aria-hidden='true' className={cn('h-auto w-4 min-w-4 border-x-0 border-b-0 p-0', sticky && 'sticky left-0 z-10 bg-white')} />;
}

interface HeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  divider?: boolean;
  sort?: { direction: SortDirection | null; onSort: () => void };
  /** Which header-band edges this cell sits on. */
  edge?: 'top' | 'bottom' | 'both';
}

export function Head({ divider = true, sort, edge = 'both', className, children, ...props }: HeadProps) {
  const SortIcon = sort?.direction === 'asc' ? ChevronUp : sort?.direction === 'desc' ? ChevronDown : ChevronsUpDown;
  return (
    <TableHead
      scope='col'
      aria-sort={sort ? (sort.direction === 'asc' ? 'ascending' : sort.direction === 'desc' ? 'descending' : 'none') : undefined}
      className={cn(
        'h-auto border-x-0 border-gray-300 bg-gray-50 py-2 pr-3 pl-0 text-left align-middle text-xs font-medium leading-4 whitespace-nowrap text-gray-900',
        edge !== 'bottom' ? 'border-t' : 'border-t-0',
        edge !== 'top' ? 'border-b' : 'border-b-0',
        className,
      )}
      {...props}
    >
      <div className={cn('flex min-h-6 items-center gap-2.5', divider && DIVIDER)}>
        {sort ? (
          <button type='button' onClick={sort.onSort} className='flex items-center gap-2.5 rounded focus-visible:outline-2 focus-visible:outline-offset-2'>
            {children}
            <SortIcon aria-hidden='true' className={cn('size-3.5', sort.direction ? 'text-ink' : 'text-gray-800')} />
          </button>
        ) : (
          children
        )}
      </div>
    </TableHead>
  );
}

/** Header for a column group: label, dashed rule, then the sub-column labels in the row below. */
export function GroupHead({ label, colSpan, className }: { label: string; colSpan: number; className?: string }) {
  return (
    <TableHead
      scope='colgroup'
      colSpan={colSpan}
      className={cn('h-auto border-x-0 border-t border-b-0 border-gray-300 bg-gray-50 pt-1 pr-3 pl-0 text-left text-xs font-medium leading-4 whitespace-nowrap text-gray-900', className)}
    >
      <div className={DIVIDER}>
        <div className='flex h-6 items-center border-b border-dashed border-gray-500 pb-0'>{label}</div>
      </div>
    </TableHead>
  );
}

interface CellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  divider?: boolean;
  /** Divider runs the cell's full height (stacked cells) instead of the short 16px rule. */
  tallDivider?: boolean;
}

export function Cell({ divider = true, tallDivider = false, className, children, ...props }: CellProps) {
  return (
    <TableCell
      className={cn('h-12 border-x-0 border-b border-gray-300 py-1 pr-3 pl-0 align-middle text-[13px] leading-[18px] whitespace-nowrap text-ink', className)}
      {...props}
    >
      <div className={cn('min-h-6 content-center', divider && (tallDivider ? 'border-l border-gray-500 pl-2.5' : DIVIDER))}>{children}</div>
    </TableCell>
  );
}

/** A row name that opens and closes the row's sub-rows. */
export function ExpandToggle({ name, expanded, onToggle }: { name: string; expanded: boolean; onToggle: () => void }) {
  return (
    <button
      type='button'
      aria-expanded={expanded}
      onClick={onToggle}
      // Open: the tree line starts under the chevron and runs into the sub-rows.
      className={cn('-ml-1 flex items-center gap-1 rounded text-left focus-visible:outline-2 focus-visible:outline-offset-2', expanded && 'after:absolute after:top-[calc(50%+8px)] after:bottom-0 after:left-[3px] after:w-px after:bg-gray-500')}
    >
      <ChevronRight aria-hidden='true' className={cn('size-3.5 shrink-0 text-gray-700 transition-transform duration-200', expanded && 'rotate-90')} />
      <span className='whitespace-normal'>{name}</span>
    </button>
  );
}

/** Name cell of a sub-row: a branch off the parent's tree line, regular weight; the last sub-row ends the line. */
export const SUB_ROW_NAME =
  'tree-sub pl-[18px] font-normal before:absolute before:-top-px before:bottom-1/2 before:left-[3px] before:w-px before:bg-gray-500 after:absolute after:top-1/2 after:left-[3px] after:h-px after:w-2.5 after:bg-gray-500 [tr:has(+tr>.tree-sub)>&]:before:bottom-0';
